import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { usePerformanceGoals } from "./hooks/usePerformanceData";
import { GoalsTable } from "./GoalsTable";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useSupervisorSync } from "./hooks/useSupervisorSync";

const PersonalGoalsTab = () => {
  const [showFreezeDialog, setShowFreezeDialog] = useState(false);
  const [isFreezing, setIsFreezing] = useState(false);
  const [showTargetValueDialog, setShowTargetValueDialog] = useState(false);
  const [goalsWithZeroTarget, setGoalsWithZeroTarget] = useState<string[]>([]);
  const {
    data: goals,
    isLoading,
    refetch
  } = usePerformanceGoals();

  // Sync supervisor_id for all personal goals
  useSupervisorSync(goals || []);

  // Calculate metrics with proper handling for frozen goals
  const totalGoals = goals?.length || 0;
  const activeGoals = goals?.filter(goal => goal.status === 'active').length || 0;
  const completedGoals = goals?.filter(goal => goal.status === 'completed').length || 0;
  const frozenGoals = goals?.filter(goal => goal.is_frozen).length || 0;
  const areGoalsFrozen = frozenGoals === totalGoals && totalGoals > 0;
  const hasGoals = totalGoals > 0;

  // Calculate average progress for all goals
  const averageProgress = goals && goals.length > 0 ? Math.round(goals.reduce((sum, goal) => sum + goal.current_progress, 0) / goals.length) : 0;
  
  if (isLoading) {
    return <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse">Loading performance goals...</div>
      </div>;
  }
  
  const handleValidateAndOpenFreezeDialog = () => {
    if (!goals) return;
    const zeroTargetGoals = goals.filter(goal => !goal.target_value || Number(goal.target_value) === 0).map(goal => goal.goal_title);
    if (zeroTargetGoals.length > 0) {
      setGoalsWithZeroTarget(zeroTargetGoals);
      setShowTargetValueDialog(true);
      return;
    }
    setShowFreezeDialog(true);
  };
  
  const handleFreezeGoals = async () => {
    setIsFreezing(true);
    try {
      const {
        data: {
          user
        },
        error: userError
      } = await supabase.auth.getUser();
      if (userError || !user) {
        toast.error("Authentication required. Please log in and try again.");
        return;
      }
      const {
        error
      } = await supabase.from('performance_goals').update({
        is_frozen: true
      }).eq('user_id', user.id);
      if (error) {
        console.error("Error freezing goals:", error);
        toast.error("Failed to freeze goals. Please try again.");
        return;
      }
      toast.success("All goals have been frozen successfully!");
      await refetch();
      setShowFreezeDialog(false);
    } catch (error) {
      console.error("Error freezing goals:", error);
      toast.error("An unexpected error occurred while freezing goals.");
    } finally {
      setIsFreezing(false);
    }
  };
  
  return <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Personal Goals</h2>
        <Button onClick={handleValidateAndOpenFreezeDialog} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={areGoalsFrozen || !hasGoals}>
          <Lock className="h-4 w-4 mr-2" />
          {areGoalsFrozen ? "Goals Frozen" : "Freeze Goals"}
        </Button>
      </div>

      {/* Goal Cards with Reduced Size */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/30 border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-white flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="text-xl font-bold text-blue-400">{totalGoals}</div>
                <span className="text-[13px]">Total Goals</span>
              </div>
              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/50 text-xs">
                {activeGoals} active
              </Badge>
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-green-600/30 border-green-500/30 hover:shadow-lg hover:shadow-green-500/25 transition-all duration-300">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-white flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="text-xl font-bold text-green-400">{activeGoals}</div>
                <span className="text-[13px]">Active Goals</span>
              </div>
              <Badge className="bg-green-500/20 text-green-300 border-green-500/50 text-xs">
                In progress
              </Badge>
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/30 border-purple-500/30 hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-white flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="text-xl font-bold text-purple-400">{averageProgress}%</div>
                <span className="text-[13px]">On Track</span>
              </div>
              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/50 text-xs">
                Avg progress
              </Badge>
            </CardTitle>
          </CardHeader>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/30 border-yellow-500/30 hover:shadow-lg hover:shadow-yellow-500/25 transition-all duration-300">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-white flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className="text-xl font-bold text-yellow-400">{completedGoals}</div>
                <span className="text-[13px]">Completed</span>
              </div>
              <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/50 text-xs">
                Achieved
              </Badge>
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* SMART Goals Table */}
      <div className="border-2 border-primary rounded-lg">
        <GoalsTable goals={goals || []} onRefresh={refetch} />
      </div>

      {/* Target Value Validation Dialog */}
      <AlertDialog open={showTargetValueDialog} onOpenChange={setShowTargetValueDialog}>
        <AlertDialogContent className="bg-white border-red-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                ✕
              </div>
              Cannot Freeze Goals
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              The following goals cannot have a target value of zero:
              <ul className="list-disc list-inside mt-2">
                {goalsWithZeroTarget.map((goalTitle, index) => <li key={index} className="text-red-600">{goalTitle}</li>)}
              </ul>
              Please update the target values before freezing your goals.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowTargetValueDialog(false)} className="bg-red-600 hover:bg-red-700 text-black w-[100px]">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showFreezeDialog} onOpenChange={setShowFreezeDialog}>
        <AlertDialogContent className="bg-white border-blue-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-blue-700 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Freeze All Goals
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-700">
              Are you sure you want to freeze all your performance goals? Once frozen, goals cannot be edited or deleted until unfrozen by an administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-400 text-black hover:bg-gray-100">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleFreezeGoals} disabled={isFreezing} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isFreezing ? "Freezing..." : "Freeze Goals"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};

export default PersonalGoalsTab;
